'use client';

import { useEffect, useState } from 'react';
import { format, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Calendar as CalendarIcon, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface DateTimePickerProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
  disableToday?: boolean;
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {return min;}
  return Math.min(Math.max(value, min), max);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = 'Выберите дату и время',
  disabled = false,
  className,
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = false,
  disableToday = false,
}: DateTimePickerProps) {
  const today = startOfDay(new Date());

  const [hourStr, setHourStr] = useState<string>(value ? pad2(value.getHours()) : '12');
  const [minuteStr, setMinuteStr] = useState<string>(value ? pad2(value.getMinutes()) : '00');

  useEffect(() => {
    if (value) {
      setHourStr(pad2(value.getHours()));
      setMinuteStr(pad2(value.getMinutes()));
    }
  }, [value]);

  const isDateDisabled = (date: Date) => {
    const day = startOfDay(date);

    if (disablePast && day < today) {return true;}
    if (disableFuture && day > today) {return true;}
    if (disableToday && day.getTime() === today.getTime()) {return true;}
    if (minDate && day < startOfDay(minDate)) {return true;}
    if (maxDate && day > startOfDay(maxDate)) {return true;}

    return false;
  };

  const commit = (date: Date, hour: number, minute: number) => {
    const next = new Date(date);
    next.setHours(clamp(hour, 0, 23));
    next.setMinutes(clamp(minute, 0, 59));
    next.setSeconds(0);
    next.setMilliseconds(0);
    onChange(next);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      onChange(null);
      return;
    }

    const h = value ? value.getHours() : clamp(Number(hourStr), 0, 23);
    const m = value ? value.getMinutes() : clamp(Number(minuteStr), 0, 59);
    commit(date, h, m);
  };

  const handleHourInput = (raw: string) => {
    setHourStr(raw);
    if (raw === '' || !value) {return;}
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {return;}
    commit(value, clamp(parsed, 0, 23), clamp(Number(minuteStr) || 0, 0, 59));
  };

  const handleMinuteInput = (raw: string) => {
    setMinuteStr(raw);
    if (raw === '' || !value) {return;}
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {return;}
    commit(value, clamp(Number(hourStr) || 0, 0, 23), clamp(parsed, 0, 59));
  };

  const handleHourBlur = () => {
    const h = clamp(Number(hourStr) || 0, 0, 23);
    setHourStr(pad2(h));
    if (value) {
      commit(value, h, clamp(Number(minuteStr) || 0, 0, 59));
    }
  };

  const handleMinuteBlur = () => {
    const m = clamp(Number(minuteStr) || 0, 0, 59);
    setMinuteStr(pad2(m));
    if (value) {
      commit(value, clamp(Number(hourStr) || 0, 0, 23), m);
    }
  };

  const handleClear = () => {
    onChange(null);
    setHourStr('12');
    setMinuteStr('00');
  };

  const applyPreset = (preset: string) => {
    const [h, m] = preset.split(':').map(Number);
    const safeH = clamp(h ?? 0, 0, 23);
    const safeM = clamp(m ?? 0, 0, 59);
    setHourStr(pad2(safeH));
    setMinuteStr(pad2(safeM));
    if (value) {
      commit(value, safeH, safeM);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-empty={!value}
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal data-[empty=true]:text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, 'PPP, HH:mm', { locale: ru }) : <span>{placeholder}</span>}
          {value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Очистить"
              className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded hover:bg-accent"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClear();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={handleDateSelect}
          locale={ru}
          disabled={isDateDisabled}
          defaultMonth={value ?? undefined}
          initialFocus
        />
        <div className="border-t p-3 space-y-2">
          <Label className="text-xs text-muted-foreground">Время</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={23}
              step={1}
              inputMode="numeric"
              className="h-9 w-16 text-center"
              value={hourStr}
              onChange={(e) => handleHourInput(e.target.value)}
              onBlur={handleHourBlur}
              onFocus={(e) => e.target.select()}
              aria-label="Часы"
            />
            <span className="text-lg font-medium text-muted-foreground">:</span>
            <Input
              type="number"
              min={0}
              max={59}
              step={1}
              inputMode="numeric"
              className="h-9 w-16 text-center"
              value={minuteStr}
              onChange={(e) => handleMinuteInput(e.target.value)}
              onBlur={handleMinuteBlur}
              onFocus={(e) => e.target.select()}
              aria-label="Минуты"
            />
            <div className="ml-auto flex gap-1">
              {(['09:00', '12:00', '18:00'] as const).map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => applyPreset(preset)}
                >
                  {preset}
                </Button>
              ))}
            </div>
          </div>
          {!value && (
            <p className="text-xs text-muted-foreground">
              Сначала выберите дату в календаре.
            </p>
          )}
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={handleClear}
            >
              Очистить
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
